import { useCallback, useRef, useState } from "react";
import type { ChangeEvent, DragEvent } from "react";
import { ACCEPTED_EXTENSION, MAX_UPLOAD_SIZE_BYTES } from "../utils/constants";
import { formatBytes } from "../utils/format";

interface DropzoneProps {
    onFileSelected: (file: File) => void;
    disabled?: boolean;
    /** Set while an upload request is in flight, for the loading state. */
    uploading?: boolean;
}

function validate(file: File): string | null {
    if (!file.name.toLowerCase().endsWith(ACCEPTED_EXTENSION)) {
        return "Sono accettati solo file PDF (estensione .pdf).";
    }
    if (file.size === 0) {
        return "Il file selezionato è vuoto.";
    }
    if (file.size > MAX_UPLOAD_SIZE_BYTES) {
        return `Il file supera la dimensione massima di ${formatBytes(MAX_UPLOAD_SIZE_BYTES)}.`;
    }
    return null;
}

export default function Dropzone({ onFileSelected, disabled = false, uploading = false }: DropzoneProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [dragOver, setDragOver] = useState(false);
    const [localError, setLocalError] = useState<string | null>(null);

    const handleFile = useCallback(
        (file: File | undefined) => {
            if (!file) return;
            const error = validate(file);
            if (error) {
                setLocalError(error);
                return;
            }
            setLocalError(null);
            onFileSelected(file);
        },
        [onFileSelected],
    );

    const onDrop = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setDragOver(false);
        if (disabled || uploading) return;
        handleFile(event.dataTransfer.files?.[0]);
    };

    const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        handleFile(event.target.files?.[0]);
        event.target.value = ""; // allow re-selecting the same file
    };

    return (
        <div>
            {/*
                Deliberately not role="button": the real <button> below already
                gives the whole control a keyboard- and screen-reader-accessible
                way to open the file picker. Nesting an interactive role="button"
                around it would create an ambiguous/duplicated accessible name
                and is invalid ARIA (interactive controls must not nest). This
                outer element only adds drag-and-drop and a mouse click
                convenience on top.
            */}
            <div
                onClick={() => !disabled && !uploading && inputRef.current?.click()}
                onDragOver={(e) => {
                    e.preventDefault();
                    if (!disabled && !uploading) setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                className={`flex min-h-[160px] cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors ${
                    dragOver
                        ? "border-brand-500 bg-brand-50 dark:bg-brand-950/30"
                        : "border-gray-300 dark:border-gray-700"
                } ${disabled || uploading ? "cursor-not-allowed opacity-60" : "hover:border-brand-400"}`}
            >
                <svg
                    aria-hidden="true"
                    className="h-10 w-10 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                    />
                </svg>

                <p className="text-sm text-gray-600 dark:text-gray-300">
                    <span className="hidden sm:inline">Trascina qui il tuo PDF oppure </span>
                    <span className="font-semibold text-brand-600 dark:text-brand-400">
                        scegli un file
                    </span>
                </p>
                <p className="text-xs text-gray-400">
                    Formato supportato: PDF &middot; dimensione massima {formatBytes(MAX_UPLOAD_SIZE_BYTES)}
                </p>

                <label className="sr-only" htmlFor="pdf-upload-input">
                    Seleziona un file PDF da convertire
                </label>
                <input
                    ref={inputRef}
                    id="pdf-upload-input"
                    type="file"
                    accept=".pdf,application/pdf"
                    className="sr-only"
                    disabled={disabled || uploading}
                    onChange={onInputChange}
                />

                <button
                    type="button"
                    disabled={disabled || uploading}
                    onClick={(e) => {
                        e.stopPropagation();
                        inputRef.current?.click();
                    }}
                    className="mt-1 inline-flex min-h-touch min-w-touch items-center justify-center rounded-lg bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {uploading ? "Caricamento in corso..." : "Seleziona file PDF"}
                </button>
            </div>

            {localError && (
                <p role="alert" className="mt-2 text-sm text-red-600 dark:text-red-400">
                    {localError}
                </p>
            )}
        </div>
    );
}
